<script setup>
import { ref, computed } from "vue";

const props = defineProps({
  showKeyboard: {
    type: Boolean,
    required: true,
  },
});

defineEmits(["mousemove"]);

const scrollableContainer = ref(null);

const windowClasses = computed(() => ({
  "search-layout--window": true,
  "search-layout--window--keyboard": props.showKeyboard,
}));

function scrollToTop() {
  scrollableContainer.value.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}

defineExpose({
  scrollToTop,
});
</script>

<template>
  <div class="search-layout" role="search" @mousemove="$emit('mousemove')">
    <div class="search-layout--top">
      <slot name="input" />
    </div>

    <div
      v-if="$slots['empty-results'] && !showKeyboard"
      class="search-layout--empty-results"
    >
      <div class="search-layout--empty-results__inner">
        <slot name="empty-results" />
      </div>
    </div>

    <div class="search-layout--bottom">
      <div ref="scrollableContainer" class="search-layout--scrollable">
        <slot name="results" />

        <div
          v-if="$slots['empty-results'] && showKeyboard"
          class="search-layout--empty-results"
        >
          <div class="search-layout--empty-results__inner">
            <slot name="empty-results" />
          </div>
        </div>
      </div>

      <div :class="windowClasses">
        <div class="search-layout--thumbnail">
          <slot name="thumbnail" />
        </div>

        <div
          v-if="showKeyboard"
          class="search-layout--keyboard"
          data-testid="search-layout-keyboard"
        >
          <slot name="keyboard" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/v2/styles/main";

.search-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 100%;
  position: relative;
  gap: var(--spacing-base-lg);
  overflow-x: hidden;

  &--top {
    height: 95px;
    width: 100%;
    flex-shrink: 0;
    position: relative;
    background: var(--search-input-surface);
    padding: var(--spacing-base);

    @include min-width-sm-and-height-xs {
      padding: var(--spacing-base-lg);
    }

    @include min-width-sm {
      height: 150px;
    }
  }

  &--bottom {
    display: flex;
    width: 100%;
    position: relative;
    height: 100%;
    overflow: hidden;
    padding: 0 var(--spacing-base);
    gap: var(--spacing-base-sm);
    flex-direction: column;

    @include min-width-xs-and-height-xs {
      padding: 0 var(--spacing-md);
      gap: var(--spacing-sm);
    }

    @include min-width-md {
      flex-direction: row;
    }
  }

  &--scrollable {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden scroll;
    padding-bottom: 300px;
    flex-shrink: 1;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &--window {
    display: none; // hide on mobile portrait

    @include min-width-sm {
      display: flex;
      flex-direction: column;
      height: 100%;
      position: relative;
      width: 625px;
    }

    &--keyboard {
      display: none;

      @include min-width-sm-and-height-xs {
        display: flex;
        width: 625px;
      }
    }
  }

  &--thumbnail {
    display: none;

    @include min-width-md {
      display: flex;
      height: 100%;
      overflow: hidden;
      flex-grow: 1;
      align-items: center;
      justify-content: center;
    }
  }

  &--keyboard {
    background: var(--search-input-surface);
    padding: 10px;
    border-radius: 10px;
    margin-bottom: var(--spacing-base-lg);
    flex-shrink: 0;
    height: 300px;

    @include massive-screen {
      @include min-width-sm {
        margin-bottom: 20dvh;
        width: 100%;
        margin-left: 13dvw;
      }

      @include min-width-md {
        margin-bottom: var(--spacing-base-lg);
        width: inherit;
        margin-left: inherit;
      }
    }
  }

  &--empty-results {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;

    &__inner {
      max-width: grid-column(4);
      text-wrap: pretty;

      @include min-width-sm {
        max-width: 30rem;
      }
    }

    @include massive-screen {
      @include min-width-sm {
        margin-bottom: calc(-1 * 40dvh);
        z-index: 1;
      }

      @include min-width-md {
        margin-bottom: 0;
        z-index: inherit;
      }
    }
  }
}

@include massive-screen {
  @include min-width-sm {
    :global(.slide-menu-button--overlay) {
      margin-top: calc(50% + 96px);
    }
  }

  @include min-width-md {
    :global(.slide-menu-button--overlay) {
      margin-top: 0;
    }
  }
}
</style>
